/**
 * Hinge-style agree / disagree lines from overlapping questionnaire answers.
 * Only compares fields where BOTH sides answered — silence never blocks.
 */

import type { ProfileAnswers, YesNo } from "../domain/answers";

export type OverlapLine = {
  /** i18n key under match.overlapItems.* */
  key: string;
  vars?: Record<string, string | number>;
};

export type ProfileOverlap = {
  agree: OverlapLine[];
  disagree: OverlapLine[];
};

type FieldDef = {
  key: string;
  read: (a: ProfileAnswers) => YesNo;
};

const YESNO_FIELDS: FieldDef[] = [
  { key: "keepsCommandments", read: (a) => a.faith?.keepsCommandments ?? null },
  { key: "spreadsGospel", read: (a) => a.faith?.spreadsGospel ?? null },
  { key: "grewUpInChurch", read: (a) => a.faith?.grewUpInChurch ?? null },
  { key: "peacemaker", read: (a) => a.character?.peacemaker ?? null },
  { key: "homebuilder", read: (a) => a.character?.homebuilder ?? null },
  { key: "caringAffectionate", read: (a) => a.character?.caringAffectionate ?? null },
  { key: "wantsPeacefulHome", read: (a) => a.character?.wantsPeacefulHome ?? null },
  { key: "goodManners", read: (a) => a.character?.goodManners ?? null },
  { key: "humorous", read: (a) => a.character?.humorous ?? null },
  { key: "herbKnowledge", read: (a) => a.home?.herbKnowledge ?? null },
  { key: "plantsOwnFruitVeg", read: (a) => a.home?.plantsOwnFruitVeg ?? null },
  { key: "interestedInFruitForest", read: (a) => a.home?.interestedInFruitForest ?? null },
  { key: "preppingForCalamities", read: (a) => a.home?.preppingForCalamities ?? null },
  { key: "communityFarming", read: (a) => a.home?.communityFarming ?? null },
  { key: "canningAndPreserving", read: (a) => a.home?.canningAndPreserving ?? null },
  { key: "survivalSkills", read: (a) => a.home?.survivalSkills ?? null },
  { key: "readyForRelationship", read: (a) => a.history?.readyForLastingRelationship ?? null },
  { key: "stableChildhood", read: (a) => a.history?.stableLovingChildhood ?? null },
];

const MAX_AGREE = 6;
const MAX_DISAGREE = 4;

function sharedInterestCount(a: ProfileAnswers, b: ProfileAnswers): number {
  const ia = a.interests ?? [];
  const ib = b.interests ?? [];
  if (!ia.length || !ib.length) return 0;
  const setB = new Set(ib);
  return ia.filter((x) => setB.has(x)).length;
}

/**
 * Compare two answered profiles and return short agree / disagree bullets.
 * Returns empty lists when either side has no questionnaire.
 */
export function profileAnswerOverlap(
  self: ProfileAnswers,
  other: ProfileAnswers
): ProfileOverlap {
  const agree: OverlapLine[] = [];
  const disagree: OverlapLine[] = [];

  if (!self || !other) return { agree, disagree };

  for (const field of YESNO_FIELDS) {
    const a = field.read(self);
    const b = field.read(other);
    if (a == null || b == null) continue;
    if (a === b) {
      if (agree.length < MAX_AGREE) agree.push({ key: field.key });
    } else if (disagree.length < MAX_DISAGREE) {
      disagree.push({ key: field.key });
    }
  }

  const shared = sharedInterestCount(self, other);
  if (shared >= 2 && agree.length < MAX_AGREE) {
    agree.push({ key: "sharedInterests", vars: { count: shared } });
  }

  const faithA = self.faith?.servingImportance;
  const faithB = other.faith?.servingImportance;
  if (
    faithA != null &&
    faithB != null &&
    Math.abs(faithA - faithB) <= 15 &&
    agree.length < MAX_AGREE
  ) {
    agree.push({ key: "faithIntensity" });
  }

  return { agree, disagree };
}
